import React, { useEffect, useState } from 'react';
import TenantBrandLockup from '../brand/TenantBrandLockup';
import { authenticatedTenantBrand } from '../brand/tenantBrand';
import { isNativeShell } from '../auth/authRedirect';
import { getNativeBiometricStatus, setNativeBiometricEnabled } from '../native/nativeSecureStorage';
import NotificationPreferences from '../notifications/NotificationPreferences';

const THEME_STORAGE_KEY = 'smfd-line-medic-theme';
const APP_VERSION = '1.3.0';
const BUNDLE_ID = 'app.sacmetrolinemedic.ios';

function currentTheme() {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage?.getItem(THEME_STORAGE_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function setTheme(theme) {
  document.documentElement.dataset.theme = theme;
  window.localStorage?.setItem(THEME_STORAGE_KEY, theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#09100e' : '#edf2f4');
}

function accessLabel(profile) {
  const memberKind = String(profile?.member_kind || 'line_medic');
  if (memberKind === 'rems_temporary') return 'Temporary REMS member · Admin verification pending';
  if (memberKind === 'rems_permanent') return 'Verified REMS member';
  if (profile?.is_admin) return 'Program administrator';
  return 'Line Medic member';
}

export default function AppSettings({ profile, onSignOut, onProfileChanged }) {
  const nativeShell = isNativeShell();
  const brand = authenticatedTenantBrand(profile);
  const [theme, updateTheme] = useState(currentTheme);
  const [biometric, setBiometric] = useState({ ready: !nativeShell, available: false, enabled: false, label: 'Biometrics', message: '' });
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [biometricMessage, setBiometricMessage] = useState('');
  const access = accessLabel(profile);

  useEffect(() => {
    if (!nativeShell) return undefined;
    let mounted = true;
    getNativeBiometricStatus().then(next => {
      if (mounted) setBiometric({ ...next, ready: true });
    });
    return () => {
      mounted = false;
    };
  }, [nativeShell]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    updateTheme(next);
    setTheme(next);
  };

  const toggleBiometric = async () => {
    setBiometricBusy(true);
    setBiometricMessage('');
    const next = await setNativeBiometricEnabled(!biometric.enabled);
    setBiometric({ ...next, ready: true });
    if (next.enabled) {
      setBiometricMessage(`${next.label} unlock is enabled on this iPhone.`);
    } else if (biometric.enabled) {
      setBiometricMessage('Biometric unlock is off. Your normal password sign-in still works.');
    } else if (next.message) {
      setBiometricMessage(next.message);
    }
    setBiometricBusy(false);
  };

  return (
    <div className="app-container" style={{ maxWidth: 920, marginLeft: 'auto', marginRight: 'auto', paddingBottom: 48 }}>
      <button className="back-button" type="button" onClick={() => window.location.assign('/')}>← Dashboard</button>

      <section className="dashboard-subpage settings-page" style={{ marginTop: 14 }}>
        <div className="dashboard-subpage-head">
          <div>
            <span className="section-kicker">ACCOUNT & APP</span>
            <h2>Settings</h2>
            <p>Account information, appearance, notifications, support, privacy, and app details.</p>
          </div>
          <TenantBrandLockup profile={profile} compact />
        </div>

        <div className="tracking-card" style={{ marginTop: 16 }}>
          <h3>Signed in account</h3>
          <p><strong>{profile?.full_name || 'Member'}</strong></p>
          <p>{profile?.email || 'Organization account'}</p>
          <p style={{ opacity: .75 }}>{access}</p>
          {brand.coBranded && <p style={{ opacity: .75 }}>Organization: <strong>{brand.shortTenantName}</strong></p>}
        </div>

        {nativeShell && (
          <div className="tracking-card" style={{ marginTop: 14 }}>
            <h3>Device unlock</h3>
            {!biometric.ready ? (
              <p>Checking biometric availability…</p>
            ) : biometric.available ? (
              <>
                <p>Use <strong>{biometric.label}</strong> to unlock the app with the secure session already stored in the iPhone Keychain.</p>
                <p style={{ opacity: .75 }}>Current status: <strong>{biometric.enabled ? 'Enabled' : 'Off'}</strong></p>
                <button className="tracking-btn" type="button" disabled={biometricBusy} onClick={toggleBiometric}>
                  {biometricBusy ? 'Working…' : biometric.enabled ? `Turn Off ${biometric.label}` : `Enable ${biometric.label}`}
                </button>
              </>
            ) : (
              <p>{biometric.message || 'Face ID or Touch ID is not available or enrolled on this iPhone.'}</p>
            )}
            {biometricMessage && <p style={{ marginTop: 10, opacity: .8 }}>{biometricMessage}</p>}
          </div>
        )}

        <NotificationPreferences profile={profile} onProfileChanged={onProfileChanged} />

        <div className="tracking-card" style={{ marginTop: 14 }}>
          <h3>Appearance</h3>
          <p>Current appearance: <strong>{theme === 'dark' ? 'Dark' : 'Light'}</strong></p>
          <button className="smfd-theme-toggle" type="button" onClick={toggleTheme}>
            <span className="smfd-theme-toggle-icon" aria-hidden="true">{theme === 'dark' ? '☾' : '☀'}</span>
            <span className="smfd-theme-toggle-label">Switch to {theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
        </div>

        <div className="tracking-card" style={{ marginTop: 14 }}>
          <h3>Help & privacy</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button className="tracking-btn" type="button" onClick={() => window.location.assign('/support?from=settings')}>Support</button>
            <button className="tracking-btn" type="button" onClick={() => window.location.assign('/privacy?from=settings')}>Privacy Policy</button>
          </div>
        </div>

        <div className="tracking-card" style={{ marginTop: 14 }}>
          <h3>App information</h3>
          <p><strong>{brand.displayName}</strong></p>
          <p>Product: Line Medic</p>
          <p>Version {APP_VERSION}</p>
          <p style={{ opacity: .7, wordBreak: 'break-all' }}>{BUNDLE_ID}</p>
          <p style={{ opacity: .7 }}>Wildland medical operations platform</p>
        </div>

        <button className="smfd-signout" type="button" onClick={onSignOut} style={{ marginTop: 18 }}>Sign out</button>
      </section>
    </div>
  );
}
